/**
 * router/index.ts —— 路由入口：hashchange → parse → guards → render
 * 来历：一期 [SEC:router] 段的 route() / go()，拆成三个文件后的装配层。
 *
 * 流程固定四步，⚠ 顺序不能换：
 *   parseHash(纯函数)  →  applyGuards(同步，可能改写 hash)  →  renderView(异步)
 *                              ↑ 改写后立即 return，让 hashchange 再走一遍
 *
 * 为什么改写完要 return 而不是直接继续渲染：保证 **hash 与 state 永远一致**。
 * 直接继续渲染的话，地址栏是 #/reports、界面却是第 3 期，用户一刷新就跳走。
 */
import { parseHash } from './parse.js';
import { applyGuards } from './guards.js';
import { renderView } from './render.js';
import { guardLeave } from '../shared/events.js';

export function route(): void {
  const r = parseHash(location.hash);
  const g = applyGuards(r);
  if (g.kind === 'redirect') {
    location.hash = g.hash;
    return; // hashchange 会再走一遍
  }
  void renderView(g.route);
}

/**
 * 跳转。⚠ 目标 hash 与当前相同时 hashchange 不触发，要手动跑一遍（一期原实现）。
 *
 * 🔴 【W2-6】所有跳转先过一次**离开拦截**（`shared/events.ts` 的 guardLeave）：
 *   双周报编辑页有未保存内容时要二次确认——二期不保存草稿，离开就丢。
 *   放在 go() 里而不是各调用点，是为了让侧栏、面包屑、页内按钮**走同一条路**
 *   （一期是在全局 click 的 [data-nav] 分支上包 rpGuard，漏掉了页内跳转）。
 * ⚠ 浏览器后退键走的是 hashchange，**拦不到**——一期同样如此，是已知边界。
 */
export function go(hash: string): void {
  guardLeave(() => {
    if (location.hash === hash || (hash === '#/' && !location.hash)) route();
    else location.hash = hash;
  });
}

export function installRouter(): void {
  window.addEventListener('hashchange', route);
}

export { retryCurrent, refreshCurrent } from './render.js';
export { parseHash } from './parse.js';
export type { Route } from './parse.js';
